/**
 * Impresión de contenido aislado (etiquetas, comprobantes) sin arrastrar el
 * layout de la aplicación.
 *
 * El diálogo del navegador imprime **el documento que llama a `print()`**. Por eso
 * no se imprime la página actual: el HTML se monta en un iframe propio y es ese
 * iframe el que dispara la impresión.
 */

/** Estilos del iframe oculto: fuera de pantalla pero con tamaño real. */
const HIDDEN_IFRAME_STYLE = [
  'position:fixed',
  'left:-10000px',
  'top:0',
  'width:210mm',
  'height:297mm',
  'border:0',
].join(';')

/** Tiempo máximo esperando imágenes/fuentes antes de abrir el diálogo. */
const ASSETS_TIMEOUT_MS = 5000

/** Margen tras `afterprint` para liberar el iframe sin cancelar el trabajo. */
const CLEANUP_DELAY_MS = 1000

/** Respaldo por si el navegador nunca dispara `afterprint`. */
const CLEANUP_FALLBACK_MS = 60000

export interface PrintHtmlOptions {
  /**
   * CSS extra inyectado al final del documento, útil para forzar el tamaño de
   * página: `@page { size: 4in 6in; margin: 0 }`. Si se omite, se imprime
   * exactamente lo que trae el HTML.
   */
  printCss?: string
  /**
   * Título del documento; varios navegadores lo usan como nombre por defecto al
   * "Guardar como PDF".
   */
  title?: string
}

/** Inserta `<style>`/`<title>` respetando el `<head>` si el HTML lo trae. */
const injectIntoHtml = (html: string, options: PrintHtmlOptions): string => {
  const extras = [
    options.title ? `<title>${options.title}</title>` : '',
    options.printCss ? `<style>${options.printCss}</style>` : '',
  ]
    .filter(Boolean)
    .join('')

  if (!extras) return html

  const headClose = html.search(/<\/head\s*>/i)
  if (headClose !== -1) {
    return html.slice(0, headClose) + extras + html.slice(headClose)
  }

  const bodyOpen = html.search(/<body[^>]*>/i)
  if (bodyOpen !== -1) {
    const bodyTagEnd = html.indexOf('>', bodyOpen) + 1
    return html.slice(0, bodyTagEnd) + extras + html.slice(bodyTagEnd)
  }

  return extras + html
}

/**
 * Espera a que carguen imágenes (los códigos de barras suelen serlo) y fuentes.
 * Sin esta espera el diálogo puede abrirse con la etiqueta a medio pintar.
 */
const waitForAssets = (win: Window): Promise<void> => {
  const doc = win.document

  const images = Array.from(doc.images ?? []).map(
    (image) =>
      new Promise<void>((resolve) => {
        if (image.complete) {
          resolve()
          return
        }
        const done = () => resolve()
        image.addEventListener('load', done, { once: true })
        image.addEventListener('error', done, { once: true })
      })
  )

  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts
  const ready: Promise<unknown>[] = [...images]
  if (fonts?.ready) ready.push(fonts.ready)

  const timeout = new Promise<void>((resolve) =>
    win.setTimeout(resolve, ASSETS_TIMEOUT_MS)
  )

  return Promise.race([Promise.all(ready).then(() => undefined), timeout])
}

/**
 * Imprime un HTML crudo (string) y nada más: monta el documento en un iframe
 * oculto, espera a que esté pintado y lanza el diálogo de impresión del
 * navegador desde ese iframe.
 *
 * La promesa resuelve cuando se abrió el diálogo (no cuando terminó de
 * imprimir): el navegador no expone el resultado del trabajo de impresión.
 */
export const printHtmlContent = (
  html: string,
  options: PrintHtmlOptions = {}
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (!html) {
      reject(new Error('No hay contenido HTML para imprimir.'))
      return
    }

    const iframe = document.createElement('iframe')
    iframe.setAttribute('style', HIDDEN_IFRAME_STYLE)
    iframe.setAttribute('aria-hidden', 'true')
    iframe.title = options.title ?? 'Impresión'

    let cleaned = false
    let fallbackTimer = 0

    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      window.clearTimeout(fallbackTimer)
      // Quitar el iframe demasiado pronto cancela el trabajo en Firefox/Safari.
      window.setTimeout(() => iframe.remove(), CLEANUP_DELAY_MS)
    }

    iframe.onload = () => {
      const win = iframe.contentWindow
      if (!win) {
        cleanup()
        reject(new Error('No se pudo preparar la vista de impresión.'))
        return
      }

      waitForAssets(win)
        .then(() => {
          win.addEventListener('afterprint', cleanup, { once: true })
          fallbackTimer = window.setTimeout(cleanup, CLEANUP_FALLBACK_MS)
          win.focus()
          win.print()
          resolve()
        })
        .catch((error) => {
          cleanup()
          reject(error)
        })
    }

    iframe.onerror = () => {
      cleanup()
      reject(new Error('No se pudo cargar el contenido a imprimir.'))
    }

    document.body.appendChild(iframe)
    iframe.srcdoc = injectIntoHtml(html, options)
  })

/**
 * Imprime lo que ya está mostrado en un iframe (por ejemplo el visor de PDF con
 * un blob URL), sin volver a pedirlo al servicio.
 */
export const printIframeContent = (
  iframe: HTMLIFrameElement | null
): boolean => {
  const win = iframe?.contentWindow
  if (!win) return false
  try {
    win.focus()
    win.print()
    return true
  } catch {
    return false
  }
}
